/**
 * Typed calls to the payment endpoints the checkout walks.
 *
 * Every money-shaped value is decided by the server and read back from its
 * response; nothing here computes an amount, a fee or a seat count. The request
 * bodies are declared in full so that a field which could be mistaken for money
 * has to be added deliberately.
 *
 * The guest token travels as `X-Guest-Token` on the calls a guest makes
 * (`06_cybersource_design.md` §3.1). It is bound server-side to one donation, so
 * presenting it against another is refused there.
 */

import { DECLINED_BY_POLICY_REASON } from '@qalam/shared';
import { ApiRequestError, apiFetch } from '@qalam/shared/frontend';
import { debugApi } from './debugLog';
import type { CaptureContextResponse } from './uc';

export type PaymentMethod = 'CARD' | 'BANK_TRANSFER';

export interface CreateDonationInput {
  /** What the donor chose, in integer cents. The only number the browser sends. */
  chosenAmountCents: number;
  method: PaymentMethod;
  coverFees?: boolean;
  hideName?: boolean;
  idempotencyKey: string;
  organizationId?: string;
  /** Present only for a guest; a signed-in donor omits it entirely. */
  guestEmail?: string;
  /** The campaign the gift is for, by slug. Resolved server-side. */
  campaignSlug?: string;
  /** Where the donor came from. Read from the link, never invented. */
  source?: string;
  /** The marketing parameters the link carried. */
  utm?: Record<string, string>;
}

export interface CreateDonationResult {
  donationId: string;
  created: boolean;
  /** Returned exactly once, on the call that created a guest donation. */
  guestToken?: string;
}

export interface AuthSetupResult {
  referenceId: string;
  accessToken?: string;
  deviceDataCollectionUrl?: string;
  /** Derived server-side from the returned URL; never hardcoded. */
  ddcOrigin?: string;
}

export interface AuthEnrollResult {
  frictionless: boolean;
  stepUpUrl?: string;
  accessToken?: string;
}

export interface PayResult {
  donationId: string;
  applied: boolean;
  seats: number;
  receiptNumber?: string;
  transactionId: string | null;
  /** Set when the gateway's answer was 202 `payment_pending`. */
  pending?: boolean;
  /** Set on a decline; the donor is offered a fresh attempt. */
  declined?: boolean;
  /**
   * Set when the gateway's answer was 202 `payment_in_review`.
   *
   * Decision Manager has the order and a person will accept or reject it.
   * Not a failure and not an unknown: the donor is told it is under review, and
   * told not to pay again.
   */
  inReview?: boolean;
  /**
   * Set when the automatic policy released the authorization and ended the
   * attempt: `declined_by_risk_policy`.
   *
   * Deliberately a different flag from `declined`. A decline is a statement
   * about a card, and the donor may reasonably try another one. This one is
   * about the order, not the card, so retrying the same card asks the question
   * that was just answered no. The screen says so and offers another card or a
   * bank transfer instead of a retry button.
   */
  declinedByPolicy?: boolean;
}

export class PaymentApiError extends Error {
  override name = 'PaymentApiError';
  readonly code: string;
  readonly status: number;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

/** Headers a guest's calls carry. Empty for a signed-in donor. */
export function guestHeaders(guestToken: string | undefined): Record<string, string> {
  return guestToken ? { 'x-guest-token': guestToken } : {};
}

function wrap(error: unknown): PaymentApiError {
  if (error instanceof ApiRequestError) {
    return new PaymentApiError(error.status, error.code, error.message);
  }
  return new PaymentApiError(
    0,
    'network_error',
    error instanceof Error ? error.message : 'network error',
  );
}

export interface PaymentClient {
  createDonation(input: CreateDonationInput): Promise<CreateDonationResult>;
  captureContext(donationId: string, guestToken?: string): Promise<CaptureContextResponse>;
  authSetup(
    donationId: string,
    transientToken: string,
    guestToken?: string,
  ): Promise<AuthSetupResult>;
  authEnroll(
    donationId: string,
    transientToken: string,
    browserFields: Record<string, unknown>,
    guestToken?: string,
  ): Promise<AuthEnrollResult>;
  pay(donationId: string, transientToken: string, guestToken?: string): Promise<PayResult>;
}

/**
 * One timed, recorded API call.
 *
 * The flight recorder sees the path, the status and the duration, and nothing
 * else: bodies carry the token, the billing and the donor's email, and none of
 * them ever reach {@link debugApi} — this helper is the only place the two
 * meet, and it hands over metadata only.
 */
async function timed<T>(path: string, run: () => Promise<T>): Promise<T> {
  const startedAt = Date.now();
  try {
    const result = await run();
    debugApi(path, 200, Date.now() - startedAt);
    return result;
  } catch (error) {
    const status = error instanceof ApiRequestError ? error.status : null;
    debugApi(path, status, Date.now() - startedAt);
    throw error;
  }
}

/** The client the app uses. Tests inject a fake rather than stubbing `fetch`. */
export function createPaymentClient(): PaymentClient {
  return {
    async createDonation(input) {
      const { guestEmail, ...rest } = input;
      const body = guestEmail ? { ...rest, guest: { email: guestEmail } } : rest;
      return timed('/api/give/donations', () =>
        apiFetch<CreateDonationResult>('/api/give/donations', {
          method: 'POST',
          body,
        }),
      );
    },

    async captureContext(donationId, guestToken) {
      const path = `/api/payments/${donationId}/capture-context`;
      return timed(path, () =>
        apiFetch<CaptureContextResponse>(path, {
          method: 'POST',
          body: {},
          headers: guestHeaders(guestToken),
        }),
      );
    },

    async authSetup(donationId, transientToken, guestToken) {
      const path = `/api/payments/${donationId}/auth-setup`;
      return timed(path, () =>
        apiFetch<AuthSetupResult>(path, {
          method: 'POST',
          body: { transientToken },
          headers: guestHeaders(guestToken),
        }),
      );
    },

    async authEnroll(donationId, transientToken, browserFields, guestToken) {
      const path = `/api/payments/${donationId}/auth-enroll`;
      return timed(path, () =>
        apiFetch<AuthEnrollResult>(path, {
          method: 'POST',
          body: { transientToken, browserFields },
          headers: guestHeaders(guestToken),
        }),
      );
    },

    async pay(donationId, transientToken, guestToken) {
      // Timed inline rather than through `timed`, because the 202 answers this
      // call exists to translate resolve as 2xx and the recorder must show the
      // status the SERVER sent, not the one `fetch` was polite about.
      const payPath = `/api/payments/${donationId}/pay`;
      const payStartedAt = Date.now();
      try {
        const raw = await apiFetch<PayResult>(payPath, {
          method: 'POST',
          body: { transientToken },
          headers: guestHeaders(guestToken),
        });
        // The 202 answers arrive as **error envelopes with a 2xx status**: the
        // API's convention is `{ error: { code } }` for every outcome that is
        // not an applied payment, and a 202 is `ok` for `fetch`. Resolved, that
        // envelope used to fall through as if it were a payment result — and
        // the flow's "not pending, not declined" branch showed the thank-you
        // screen for a payment that had not been credited. A 202 is never an
        // applied payment; read the code and answer what it says.
        const envelope = raw as { error?: { code?: string } } | null;
        const envelopeCode =
          typeof envelope === 'object' && envelope !== null ? envelope.error?.code : undefined;
        debugApi(
          payPath,
          envelopeCode === 'payment_in_review' || envelopeCode === 'payment_pending' ? 202 : 200,
          Date.now() - payStartedAt,
        );
        if (envelopeCode === 'payment_in_review') {
          return {
            donationId,
            applied: false,
            seats: 0,
            transactionId: null,
            pending: true,
            inReview: true,
          };
        }
        if (envelopeCode === 'payment_pending') {
          return { donationId, applied: false, seats: 0, transactionId: null, pending: true };
        }
        return raw;
      } catch (error) {
        if (error instanceof ApiRequestError) {
          // A decline answers 200 with `payment_declined`; a pending answer is
          // 202 `payment_pending`. Neither is a transport failure and neither
          // should reach the donor as "something went wrong".
          // A Decision Manager review is its own outcome, not a generic pending.
          // The donor's screen says "under review" rather than "we are checking",
          // because a person is deciding whether to take their money.
          if (error.code === 'payment_in_review') {
            return {
              donationId,
              applied: false,
              seats: 0,
              transactionId: null,
              pending: true,
              inReview: true,
            };
          }
          if (error.status === 202 || error.code === 'payment_pending') {
            return { donationId, applied: false, seats: 0, transactionId: null, pending: true };
          }
          if (error.code === 'payment_declined') {
            // The policy's release is its own code, checked before the generic
            // decline so the screen can tell "try another card" from "we did not
            // take your money and nothing is wrong with your card".
            const reason = error.reason;
            if (reason === DECLINED_BY_POLICY_REASON) {
              return {
                donationId,
                applied: false,
                seats: 0,
                transactionId: null,
                declined: true,
                declinedByPolicy: true,
              };
            }
            return { donationId, applied: false, seats: 0, transactionId: null, declined: true };
          }
          if (error.status === 409 && error.code === 'attempt_finished_start_new_attempt') {
            return { donationId, applied: false, seats: 0, transactionId: null, declined: true };
          }
          // A payment that is already running answers 409. It is not a decline:
          // inviting a retry here is how a second capture happens. The donor is
          // told the payment is still being worked on.
          if (error.status === 409 && error.code === 'payment_in_progress') {
            return { donationId, applied: false, seats: 0, transactionId: null, pending: true };
          }
        }
        throw wrap(error);
      }
    },
  };
}

/**
 * The browser fields Payer Auth Enroll accepts (validated by zod server-side).
 *
 * Collected here because the server refuses anything it does not declare, so a
 * key added here that the API does not know is silently dropped rather than
 * smuggled into the gateway.
 *
 * There is no `acceptHeader` here, and there never should be. A browser
 * cannot read its own document's Accept header from JavaScript, so the only
 * way to produce one is to make one up — and this file used to make up
 * `text/html (Mozilla/5.0 ...)`, which is the user agent wearing an Accept
 * header's name. The server reads the real header off the request that asks
 * for the enrollment, so the donor's browser supplies it.
 */
export function collectBrowserFields(): Record<string, unknown> {
  const nav = typeof navigator === 'undefined' ? undefined : navigator;
  const screenRef = typeof screen === 'undefined' ? undefined : screen;
  return {
    userAgent: nav?.userAgent,
    language: nav?.language,
    colorDepth: screenRef?.colorDepth,
    javaEnabled: false,
    javaScriptEnabled: true,
    screenHeight: screenRef?.height,
    screenWidth: screenRef?.width,
    timeZoneOffset: new Date().getTimezoneOffset(),
  };
}
